import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Plus, Trash2, Edit3, HelpCircle, X } from "lucide-react";

interface Tpl { id: string; name: string; subject: string; html: string; text: string | null; updatedAt: string }

export default function TemplatesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["templates"], queryFn: () => api<{ templates: Tpl[] }>("/api/templates") });
  const [editing, setEditing] = useState<Tpl | "new" | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const delMut = useMutation({
    mutationFn: (id: string) => api(`/api/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-gray-900">Templates</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHelp(true)}
            title="Template tips and merge-tag reference"
            className="bg-white border border-gray-300 text-gray-600 rounded-lg w-8 h-8 inline-flex items-center justify-center hover:bg-gray-50">
            <HelpCircle size={14} />
          </button>
          <button onClick={() => setEditing("new")}
            className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 hover:bg-indigo-700">
            <Plus size={14} /> New template
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Reusable email bodies. Click <HelpCircle size={11} className="inline -mt-0.5" /> for the full guide and merge-tag reference.
      </p>

      {showHelp && <TemplateHelpModal onClose={() => setShowHelp(false)} />}

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {(data?.templates ?? []).map((t) => (
          <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                <p className="text-xs text-gray-500 mt-1 truncate">{t.subject}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(t)} className="text-gray-400 hover:text-indigo-600 p-1"><Edit3 size={14} /></button>
                <button onClick={() => { if (confirm(`Delete "${t.name}"?`)) delMut.mutate(t.id); }}
                  className="text-gray-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-3">Updated {new Date(t.updatedAt).toLocaleDateString()}</div>
          </div>
        ))}
        {!isLoading && data?.templates.length === 0 && <p className="text-sm text-gray-400">No templates yet.</p>}
      </div>

      {editing && <Editor tpl={editing === "new" ? null : editing} onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["templates"] }); }} />}
    </div>
  );
}

type DeviceKey = "desktop" | "tablet" | "mobile";
const DEVICE_WIDTHS: Record<DeviceKey, { label: string; width: number }> = {
  desktop: { label: "Desktop", width: 760 },   // typical email-client max content width
  tablet: { label: "Tablet", width: 600 },
  mobile: { label: "Mobile", width: 360 },     // iPhone SE / typical narrow phone
};

// Whitelist of merge tags that the worker actually substitutes from
// the contact at send time. Anything outside this list will silently
// render as empty string — so we proactively warn the user when their
// subject/body uses one we don't recognize.
const KNOWN_MERGE_TAGS = ["first_name", "last_name", "email"];

interface MergeTagCheck { unknown: string[] }

function checkMergeTags(...inputs: string[]): MergeTagCheck {
  const seen = new Set<string>();
  const unknown = new Set<string>();
  for (const s of inputs) {
    const matches = s.matchAll(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g);
    for (const m of matches) {
      const tag = m[1];
      if (seen.has(tag)) continue;
      seen.add(tag);
      // custom.<anything> is fine — that's user-defined CSV columns
      if (tag.startsWith("custom.")) continue;
      if (!KNOWN_MERGE_TAGS.includes(tag)) unknown.add(tag);
    }
  }
  return { unknown: Array.from(unknown) };
}

function Editor({ tpl, onClose }: { tpl: Tpl | null; onClose: () => void }) {
  const [name, setName] = useState(tpl?.name ?? "");
  const [subject, setSubject] = useState(tpl?.subject ?? "");
  const [html, setHtml] = useState(tpl?.html ?? "<p>Hi {{first_name}},</p>\n<p>Your message here.</p>");
  const [tab, setTab] = useState<"html" | "preview">("html");
  const [device, setDevice] = useState<DeviceKey>("desktop");
  const [showMergeHelp, setShowMergeHelp] = useState(false);
  const qc = useQueryClient();

  const tagCheck = checkMergeTags(subject, html);

  const mut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({ name, subject, html });
      return tpl
        ? api(`/api/templates/${tpl.id}`, { method: "PUT", body })
        : api("/api/templates", { method: "POST", body });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); onClose(); },
  });

  const renderedSrcDoc = html
    .replace(/\{\{first_name\}\}/g, "Alice")
    .replace(/\{\{last_name\}\}/g, "Smith")
    .replace(/\{\{email\}\}/g, "alice@example.com");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{tpl ? "Edit template" : "New template"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">&times;</button>
        </div>
        <div className="px-5 py-3 border-b border-gray-200 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Template name</label>
            <input placeholder="e.g. April newsletter" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            <p className="text-[11px] text-gray-400 mt-0.5">Internal label only — recipients never see this.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Subject</label>
              <button type="button" onClick={() => setShowMergeHelp((s) => !s)}
                className="text-xs text-indigo-600 hover:underline">
                {showMergeHelp ? "Hide" : "?"} merge tags
              </button>
            </div>
            <input placeholder="e.g. Hi {{first_name}}, your update is here" value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
            <p className="text-[11px] text-gray-400 mt-0.5">Personalize with merge tags like <code className="bg-gray-100 px-1 rounded">{"{{first_name}}"}</code>.</p>
          </div>
        </div>

        {showMergeHelp && (
          <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-100 text-xs">
            <p className="font-semibold text-indigo-900 mb-1">Available merge tags</p>
            <p className="text-indigo-800 mb-2">Use these in either the Subject or HTML body. The worker substitutes them per-recipient at send time. Tags not in this list render as empty strings.</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-indigo-900 font-mono">
              <li><code>{"{{first_name}}"}</code> — contact's first name</li>
              <li><code>{"{{last_name}}"}</code> — contact's last name</li>
              <li><code>{"{{email}}"}</code> — recipient's email</li>
              <li><code>{"{{custom.<key>}}"}</code> — any custom CSV column you imported</li>
            </ul>
            <p className="text-indigo-700 mt-2 text-[11px]">The unsubscribe link in the footer is added automatically — don't put it in the body.</p>
          </div>
        )}

        {tagCheck.unknown.length > 0 && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs flex items-start gap-2">
            <span className="text-amber-700 font-bold">⚠</span>
            <div className="text-amber-800">
              <span className="font-medium">Unknown merge tag{tagCheck.unknown.length === 1 ? "" : "s"}: </span>
              {tagCheck.unknown.map((t) => <code key={t} className="bg-amber-100 px-1 rounded mx-0.5">{`{{${t}}}`}</code>)}
              <span> — these will render empty. Did you mean <code className="bg-amber-100 px-1 rounded">{"{{first_name}}"}</code>? Click "? merge tags" above for the full list.</span>
            </div>
          </div>
        )}
        <div className="px-5 pt-3 flex items-center justify-between">
          <div className="flex gap-2 text-sm">
            <button onClick={() => setTab("html")} className={`px-3 py-1 rounded ${tab === "html" ? "bg-gray-200 text-gray-900" : "text-gray-500"}`}>HTML</button>
            <button onClick={() => setTab("preview")} className={`px-3 py-1 rounded ${tab === "preview" ? "bg-gray-200 text-gray-900" : "text-gray-500"}`}>Preview</button>
          </div>
          {tab === "preview" && (
            <div className="flex items-center gap-1 text-xs">
              <span className="text-gray-500 mr-2">Width:</span>
              {(Object.keys(DEVICE_WIDTHS) as DeviceKey[]).map((k) => (
                <button key={k} onClick={() => setDevice(k)}
                  className={`px-2.5 py-1 rounded ${device === k ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                  {DEVICE_WIDTHS[k].label}
                  <span className="text-[10px] opacity-70 ml-1">{DEVICE_WIDTHS[k].width}px</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 px-5 py-3 overflow-hidden">
          {tab === "html" ? (
            <textarea value={html} onChange={(e) => setHtml(e.target.value)}
              className="w-full h-full font-mono text-sm border border-gray-300 rounded p-3 resize-none" />
          ) : (
            // Center the iframe at the chosen width inside a scrollable
            // checkered backdrop so the user clearly sees the content area
            // vs. surrounding chrome.
            <div className="w-full h-full bg-gray-100 rounded overflow-auto flex justify-center items-start py-4">
              <div className="bg-white shadow-md rounded transition-all" style={{ width: DEVICE_WIDTHS[device].width }}>
                <iframe srcDoc={renderedSrcDoc}
                  style={{ width: "100%", height: "calc(85vh - 200px)", minHeight: 400, border: 0 }}
                  title="preview" />
              </div>
            </div>
          )}
        </div>
        {mut.error && <p className="px-5 text-sm text-red-600">{(mut.error as Error).message}</p>}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-gray-500 px-3 py-1.5">Cancel</button>
          <button onClick={() => mut.mutate()} disabled={!name || !subject || mut.isPending}
            className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700 disabled:opacity-50">
            {mut.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateHelpModal({ onClose }: { onClose: () => void }) {
  const exampleHtml = `<p>Hi {{first_name}},</p>
<p>Quick update on {{custom.project_name}}.</p>
<p><a href="https://yoursite.com/dashboard">Open dashboard</a></p>
<p>— The team</p>`;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Template guide</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 text-sm text-gray-700 space-y-4">
          <div>
            <p>Templates are reusable email bodies. A campaign picks one template, fills in per-recipient merge tags, and sends. The same template can drive many campaigns.</p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Required fields</h4>
            <ul className="text-xs space-y-0.5">
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">Template name</code> — internal label only, recipients never see this</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">Subject</code> — what shows in the recipient's inbox row. Supports merge tags</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">HTML</code> — your message body. Plain-text alternative is auto-derived if you don't write one</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Merge tags</h4>
            <p className="text-xs mb-2">Substituted per recipient at send time. Tags not in this list render as empty strings (and the editor shows an amber warning).</p>
            <ul className="text-xs space-y-0.5 font-mono">
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{"{{first_name}}"}</code> — contact's first name</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{"{{last_name}}"}</code> — contact's last name</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{"{{email}}"}</code> — recipient's email address</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{"{{custom.<key>}}"}</code> — any custom CSV column you imported (e.g. <code>{"{{custom.company}}"}</code>)</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">What gets injected automatically</h4>
            <ul className="text-xs space-y-0.5">
              <li>An unsubscribe footer (button + workspace physical address) — leave it on unless you have a strong reason to disable it in Settings</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">List-Unsubscribe</code> headers so Gmail / Outlook surface their own one-click button at the top of the email</li>
              <li>Reply-To: the sender's wenmail address — replies land in their existing webmail inbox</li>
            </ul>
            <p className="text-xs text-gray-500 mt-1">Don't write your own unsubscribe link in the body — the auto-injected one is HMAC-signed and tracked in Reports.</p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Preview</h4>
            <p className="text-xs">The Preview tab renders your HTML inside an iframe with sample data substituted for merge tags. The width toggle (Desktop / Tablet / Mobile) shows how the email looks at common email-client widths — useful for checking that wide tables / images don't overflow on phones.</p>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Deliverability tips</h4>
            <ul className="text-xs space-y-0.5">
              <li>Keep HTML simple — tables and inline styles render most consistently across mail clients</li>
              <li>Always include alt text on images — many recipients have images blocked by default</li>
              <li>Don't make the email all-image-no-text — spam filters distrust it</li>
              <li>One clear call-to-action per email outperforms several</li>
              <li>Avoid spammy phrases in the subject ("FREE!!!", "URGENT", "100% off") — they trigger filter rules</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Example</h4>
            <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">{exampleHtml}</pre>
            <p className="text-xs text-gray-500 mt-1">Renders as: <em>"Hi Alice, Quick update on Notion redesign. [Open dashboard] — The team"</em> (assuming Alice has <code>{"{{custom.project_name}}"}</code> = "Notion redesign" in her contact record).</p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="bg-indigo-600 text-white rounded px-3 py-1.5 text-sm hover:bg-indigo-700">Got it</button>
        </div>
      </div>
    </div>
  );
}
